import { Button } from '@parentsquare/squarekit/components/ui/button';
import { DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@parentsquare/squarekit/components/ui/drawer';
import { Separator } from '@parentsquare/squarekit/components/ui/separator';
import { Textarea } from '@parentsquare/squarekit/components/ui/textarea';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface FeedbackDialogProps {
    readonly open: boolean;
    readonly onSubmit: (text: string) => void;
    readonly onCloseAutoFocus?: (e: Event) => void;
}

export const FeedbackDialog = ({
    open,
    onSubmit,
    onCloseAutoFocus,
}: FeedbackDialogProps): React.ReactNode => {
    const [text, setText] = useState('');
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (open) {
            setText('');
            requestAnimationFrame(() => {
                closeButtonRef.current?.focus();
            });
        }
    }, [open]);

    const handleClose = (): void => {
        setText('');
        onSubmit('');
    };

    const handleSubmit = (): void => {
        const trimmed = text.trim();
        setText('');
        onSubmit(trimmed);
    };

    return (
        <DrawerContent id="feedback-drawer" onCloseAutoFocus={onCloseAutoFocus}>
            <DrawerHeader className="tw:flex tw:flex-row tw:items-start tw:justify-between tw:text-left">
                <div className="tw:flex tw:flex-col tw:gap-1">
                    <DrawerTitle>Share Additional Feedback</DrawerTitle>
                    <p className="tw:text-sm tw:text-type-secondary">Optional</p>
                </div>
                <DrawerClose asChild>
                    <Button ref={closeButtonRef} variant="ghost" size="icon" onClick={handleClose} aria-label="Close">
                        <X className="tw:h-4 tw:w-4" />
                    </Button>
                </DrawerClose>
            </DrawerHeader>
            <Separator />
            <div className="tw:flex tw:flex-col tw:gap-3 tw:px-4 tw:py-3">
                <p className="tw:text-sm tw:font-semibold tw:text-type-primary">
                    What did you think of this response?
                </p>
                <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type feedback here"
                    rows={3}
                    maxLength={1000}
                />
            </div>
            <Separator />
            <DrawerFooter className="tw:flex-row tw:justify-end">
                <Button variant="primary" onClick={handleSubmit} disabled={text.trim().length === 0}>
                    Submit
                </Button>
            </DrawerFooter>
        </DrawerContent>
    );
};
